import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { GroupPicker } from 'components';
import { IGroupPickerProps } from 'components/domain/pickers/GroupPicker/GroupPicker';
import { DEFAULT_PICKER_PAGE_SIZE, findOrFetchPickerItem } from 'components/foundation/pickers/utils';
import { useOpenable } from 'hooks';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import { useReactQueryLazyLoading } from 'hooks/react-query/useReactQueryLazyLoading/useReactQueryLazyLoading';
import useScrollerRef from 'hooks/ui/useScrollerRef';
import { ClassName } from 'types/ClassName';
import { executeFunctionsCurried } from 'utils/functionUtils';
import { useCostCodeGroupPickerData } from './hooks/useCostCodeGroupPickerData';

export interface ICostCodeGroupPickerProps
  extends Omit<IGroupPickerProps, 'data' | 'isLoading' | 'currentGroup' | 'dataError' | 'loadedAll'> {
  className?: ClassName;
}

export default function CostCodeGroupPicker({
  value,
  includeNoneOption,
  className,
  onOpen,
  onClose,
  ...pickerProps
}: ICostCodeGroupPickerProps) {
  const toggle = useOpenable();
  const { scroller, setScrollerRef } = useScrollerRef();
  const baseKey = useReactQueryBaseKey();

  const { fetchPage, fetchSelected } = useCostCodeGroupPickerData();

  const {
    data,
    isLoading,
    isError: isDataError,
    loadedAll,
  } = useReactQueryLazyLoading(scroller, [...baseKey, 'cost-code-group-picker'], fetchPage, DEFAULT_PICKER_PAGE_SIZE, {
    enabled: toggle.isOpen,
  });

  const { data: selectedGroup } = useQuery({
    queryKey: [...baseKey, 'cost-code-group-picker-by-id', value],
    queryFn: () => findOrFetchPickerItem(value!, data, fetchSelected),
    enabled: !!value,
  });

  const classes = classNames('cost-code-picker', className);

  return (
    <GroupPicker
      currentGroup={selectedGroup?.groupName ?? null}
      includeNoneOption={includeNoneOption}
      className={classes}
      setScrollerRef={setScrollerRef}
      loadedAll={loadedAll}
      dataError={isDataError}
      data={data}
      isLoading={isLoading}
      value={value}
      onOpen={executeFunctionsCurried(toggle.open, onOpen)}
      onClose={executeFunctionsCurried(toggle.close, onClose)}
      {...pickerProps}
    />
  );
}
